import { useCallback, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useDispatch } from "react-redux";
import FileUpload from "components/common/FileUpload";
import api, { isSuccessResponse } from "lib/api";
import { API_ENDPOINTS } from "lib/apiUrl";
import swtAlert from "lib/sweetAlert";
import { UpdateAuthUserData } from "store/reducer";
import { refreshAuthUser } from "pages/settings/settingsHelper";

export default function SettingsChangeImagePage() {
  const dispatch = useDispatch();
  const [uploadItems, setUploadItems] = useState([]);
  const [savingProfile, setSavingProfile] = useState(false);

  const saveAvatarToProfile = useCallback(
    async (url) => {
      const trimmed = String(url ?? "").trim();
      if (!trimmed) return;

      setSavingProfile(true);
      try {
        const resp = await api.patch(API_ENDPOINTS.profile, { avatarUrl: trimmed });
        if (!isSuccessResponse(resp)) {
          await swtAlert.error(
            "Update failed",
            resp?.message || "Could not save your profile image."
          );
          return;
        }
        dispatch(UpdateAuthUserData({ avatarUrl: trimmed }));
        await refreshAuthUser(dispatch);
        setUploadItems([]);
        await swtAlert.success(
          "Profile image updated",
          resp?.message || "Your profile image has been saved."
        );
      } catch (err) {
        await swtAlert.error(
          "Update failed",
          err?.message || "Could not save your profile image."
        );
      } finally {
        setSavingProfile(false);
      }
    },
    [dispatch]
  );

  const handleUploadComplete = useCallback(
    (url) => {
      saveAvatarToProfile(url);
    },
    [saveAvatarToProfile]
  );

  return (
    <>
      <Helmet>
        <title>Change image | Fortify</title>
      </Helmet>
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">
          Change image
        </h1>
        <div className="mt-2 h-0.5 w-full max-w-xl bg-accent" aria-hidden />

        <div className="mt-8 max-w-xl">
          <FileUpload
            title="Upload a new profile image"
            accept="image/*"
            multiple={false}
            maxSizeMB={5}
            helperText="PNG, JPG, GIF, or WebP (max. 5 MB)."
            items={uploadItems}
            onItemsChange={setUploadItems}
            onUploadComplete={handleUploadComplete}
            showPreviewActions
            showPreviewProgress
          />
          {savingProfile ? (
            <p className="mt-3 text-sm text-muted-foreground">Saving profile image…</p>
          ) : null}
        </div>
      </div>
    </>
  );
}
